// Landing Page - Mode Selection / Smart Redirect
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuth } from '@/providers/AuthProvider';
import {
  Monitor,
  Code2,
  ArrowRight,
  Sparkles,
  FileText,
  Workflow,
  Key,
  BarChart3,
} from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { PoweredByTao } from '@/components/ui/tao-logo';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

function LandingPage() {
  const { isReady } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const response = await api.users.me();
      return (response && typeof response === 'object' && 'data' in response)
        ? (response as any).data
        : response;
    },
    enabled: isReady,
    staleTime: 5 * 60 * 1000,
  });

  // Redirect if mode already chosen
  useEffect(() => {
    if (!profileLoading && profile?.preferredMode) {
      if (profile.preferredMode === 'direct') {
        navigate({ to: '/dashboard' });
      } else if (profile.preferredMode === 'api') {
        navigate({ to: '/api-home' });
      }
    }
  }, [profile, profileLoading, navigate]);

  const setModeMutation = useMutation({
    mutationFn: (mode: 'direct' | 'api') =>
      api.users.update({ preferredMode: mode }),
    onSuccess: (_data, mode) => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      if (mode === 'direct') {
        navigate({ to: '/dashboard' });
      } else {
        navigate({ to: '/api-home' });
      }
    },
  });

  // Show loading while checking mode
  if (profileLoading || profile?.preferredMode) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background gradient-mesh noise">
        <div className="flex flex-col items-center gap-6 animate-fadeIn">
          <div className="spinner-gold" />
          <p className="text-muted-foreground font-medium tracking-wide">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background gradient-mesh noise flex flex-col">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
        <div className="absolute top-0 right-1/4 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl" />

        <div className="relative px-8 pt-16 pb-8 text-center">
          <div className="animate-slideUp">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/20 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-gold-400" />
              <span className="text-xs font-medium text-gold-400 uppercase tracking-wider">
                Choose Your Experience
              </span>
            </div>
            <h1 className="text-4xl font-display font-bold text-foreground mb-3">
              Welcome to <span className="gradient-text">CreditNova</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              How would you like to use CreditNova? You can switch between modes at any time.
            </p>
          </div>
        </div>
      </div>

      {/* Two-Card Selection */}
      <div className="flex-1 flex items-start justify-center px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl w-full mt-4">
          {/* Direct Usage Card */}
          <button
            onClick={() => setModeMutation.mutate('direct')}
            disabled={setModeMutation.isPending}
            className={cn(
              'group relative text-left p-8 rounded-2xl border transition-all duration-300',
              'glass-elevated border-border/50 hover:border-gold-500/50',
              'hover:shadow-[0_0_40px_-10px_hsl(var(--gold-500)/0.2)]',
              'animate-slideUp disabled:opacity-50'
            )}
            style={{ animationDelay: '0.1s' }}
          >
            {/* Icon */}
            <div className="mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gold-500/10 flex items-center justify-center text-gold-400 group-hover:bg-gold-500 group-hover:text-primary-950 transition-all duration-300 group-hover:shadow-glow-gold">
                <Monitor className="w-8 h-8" />
              </div>
            </div>

            {/* Content */}
            <h2 className="text-2xl font-display font-bold text-foreground mb-2 group-hover:text-gold-400 transition-colors">
              Direct Usage
            </h2>
            <p className="text-muted-foreground mb-6">
              Use CreditNova directly via web app
            </p>

            {/* Feature list */}
            <div className="space-y-3 mb-8">
              <FeatureItem icon={<Workflow className="w-4 h-4" />} text="Upload & analyze documents" />
              <FeatureItem icon={<BarChart3 className="w-4 h-4" />} text="Interactive analysis dashboard" />
              <FeatureItem icon={<FileText className="w-4 h-4" />} text="Generate detailed PDF reports" />
            </div>

            {/* CTA */}
            <div className="flex items-center gap-2 text-gold-400 font-semibold">
              <span>Get Started</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* API Usage Card */}
          <button
            onClick={() => setModeMutation.mutate('api')}
            disabled={setModeMutation.isPending}
            className={cn(
              'group relative text-left p-8 rounded-2xl border transition-all duration-300',
              'glass-elevated border-border/50 hover:border-emerald-500/50',
              'hover:shadow-[0_0_40px_-10px_hsl(var(--emerald-500)/0.2)]',
              'animate-slideUp disabled:opacity-50'
            )}
            style={{ animationDelay: '0.2s' }}
          >
            {/* Icon */}
            <div className="mb-6">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 group-hover:bg-emerald-500 group-hover:text-primary-950 transition-all duration-300 group-hover:shadow-[0_0_20px_-4px_hsl(var(--emerald-500)/0.5)]">
                <Code2 className="w-8 h-8" />
              </div>
            </div>

            {/* Content */}
            <h2 className="text-2xl font-display font-bold text-foreground mb-2 group-hover:text-emerald-400 transition-colors">
              API Usage
            </h2>
            <p className="text-muted-foreground mb-6">
              Use CreditNova via API
            </p>

            {/* Feature list */}
            <div className="space-y-3 mb-8">
              <FeatureItem icon={<FileText className="w-4 h-4" />} text="Configure analysis templates" color="emerald" />
              <FeatureItem icon={<Key className="w-4 h-4" />} text="Manage API keys & access" color="emerald" />
              <FeatureItem icon={<BarChart3 className="w-4 h-4" />} text="Track usage & billing" color="emerald" />
            </div>

            {/* CTA */}
            <div className="flex items-center gap-2 text-emerald-400 font-semibold">
              <span>Get Started</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-8 py-6 border-t border-border/30">
        <div className="flex items-center justify-between">
          <PoweredByTao />
          <p className="text-xs text-muted-foreground/50">
            {new Date().getFullYear()} TAO Solutions. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function FeatureItem({ icon, text, color = 'gold' }: { icon: React.ReactNode; text: string; color?: 'gold' | 'emerald' }) {
  return (
    <div className="flex items-center gap-3">
      <div className={cn(
        'p-1.5 rounded-lg',
        color === 'gold' ? 'bg-gold-500/10 text-gold-400' : 'bg-emerald-500/10 text-emerald-400'
      )}>
        {icon}
      </div>
      <span className="text-sm text-muted-foreground">{text}</span>
    </div>
  );
}
